import React, { useState, useEffect } from 'react'
import { Plus, X, Timer, Users, Clock, Edit, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import * as api from '../../app/axios/api.jsx'
import { toast } from 'sonner'

const BatchPage = () => {
  const navigate = useNavigate()
  const [showForm, setShowForm] = useState(false)
  const [showUpdateForm, setShowUpdateForm] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedBatch, setSelectedBatch] = useState(null)
  const [batchToDelete, setBatchToDelete] = useState(null)

  // Single state for the list of batches
  const [batches, setBatches] = useState([])

  // Fetch batches from API
  const fetchBatches = async () => {
    try {
      const res = await api.fetchBatch()
      setBatches(res.data)
    } catch (error) {
      console.error('Error fetching batches:', error)
      toast.error(error?.response?.data?.message || 'Failed to fetch batches.')
    } finally {
      setIsLoading(false)
    }
  }
  useEffect(() => {
    fetchBatches()
  }, [])

  const [formData, setFormData] = useState({
    name: '',
    batch_time_in_hour: '',
    estimated_batch_duration_in_month: '',
    status: 'running',
  })

  const statusOptions = [
    {
      value: 'running',
      label: 'Running',
      color: 'bg-green-100 text-green-800',
    },
    { value: 'inactive', label: 'Inactive', color: 'bg-red-100 text-red-800' },
    {
      value: 'completed',
      label: 'Completed',
      color: 'bg-blue-100 text-blue-800',
    },
    {
      value: 'pending',
      label: 'Pending',
      color: 'bg-yellow-100 text-yellow-800',
    },
  ]

  const getStatusDisplay = (status) => {
    const statusOption = statusOptions.find((option) => option.value === status)
    return statusOption || { label: status, color: 'bg-gray-100 text-gray-800' }
  }

  const handleInputChange = (e) => {
    const { name, value, type } = e.target
    setFormData({
      ...formData,
      [name]: type === 'number' ? parseInt(value) || '' : value,
    })
  }

  // Create new batch
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (
      !formData.name ||
      !formData.batch_time_in_hour ||
      !formData.estimated_batch_duration_in_month
    ) {
      toast.warn('Please fill in all required fields.')
      return
    }

    setIsSubmitting(true)
    try {
      const batchData = {
        name: formData.name,
        batch_time_in_hour: parseInt(formData.batch_time_in_hour),
        estimated_batch_duration_in_month: parseInt(
          formData.estimated_batch_duration_in_month
        ),
        status: formData.status,
      }

      const response = await api.createBatch(batchData)
      const newBatch = response.data

      setBatches([newBatch, ...batches])
      resetForm()
      setShowForm(false)
      toast.success('Batch created successfully!')
    } catch (error) {
      console.error('Error creating batch:', error)
      toast.error(error?.response?.data?.message || 'Failed to create batch.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Update existing batch
  const handleUpdateSubmit = async (e) => {
    e.preventDefault()
    if (
      !formData.name ||
      !formData.batch_time_in_hour ||
      !formData.estimated_batch_duration_in_month
    ) {
      toast.warn('Please fill in all required fields.')
      return
    }

    setIsSubmitting(true)
    try {
      const updateData = {
        name: formData.name,
        batch_time_in_hour: parseInt(formData.batch_time_in_hour),
        estimated_batch_duration_in_month: parseInt(
          formData.estimated_batch_duration_in_month
        ),
        status: formData.status,
      }

      const response = await api.updateBatch(selectedBatch.id, updateData)

      // Update the batch in the local state
      setBatches(
        batches.map((batch) =>
          batch.id === selectedBatch.id ? { ...batch, ...response.data } : batch
        )
      )

      resetForm()
      setShowUpdateForm(false)
      setSelectedBatch(null)
      fetchBatches()

      toast.success('Batch updated successfully!')
    } catch (error) {
      console.error('Error updating batch:', error)
      toast.error(error?.response?.data?.message || 'Failed to update batch.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Delete batch
  const handleDelete = async () => {
    if (!batchToDelete) return

    try {
      await api.deleteBatch(batchToDelete.id)

      // Remove the batch from local state
      setBatches(batches.filter((batch) => batch.id !== batchToDelete.id))

      setShowDeleteDialog(false)
      setBatchToDelete(null)
      toast.success('Batch deleted successfully!')
    } catch (error) {
      console.error('Error deleting batch:', error)
      toast.error(error?.response?.data?.message || 'Failed to delete batch.')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      batch_time_in_hour: '',
      estimated_batch_duration_in_month: '',
      status: 'running',
    })
  }

  const handleCancel = () => {
    resetForm()
    setShowForm(false)
    setShowUpdateForm(false)
    setSelectedBatch(null)
  }

  const handleUpdateClick = (batch) => {
    setSelectedBatch(batch)
    setFormData({
      name: batch.name,
      batch_time_in_hour: batch.batch_time_in_hour.toString(),
      estimated_batch_duration_in_month:
        batch.estimated_batch_duration_in_month.toString(),
      status: batch.status,
    })
    setShowUpdateForm(true)
  }

  const handleDeleteClick = (batch) => {
    setBatchToDelete(batch)
    setShowDeleteDialog(true)
  }

  const handleViewDetails = (batchId) => {
    navigate(`/batchDetails/${batchId}`)
  }

  return (
    <div className='min-h-screen bg-gray-50'>
      {/* Header */}
      <header className='bg-white shadow-sm border-b '>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-16'>
            <div className='flex items-center'>
              <h1 className='text-2xl font-bold text-gray-900'>Batches</h1>
              <span className='ml-3 bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded'>
                {batches.length} Total
              </span>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className='inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200'
            >
              <Plus className='w-4 h-4 mr-2' />
              Create New Batch
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {isLoading ? (
          <div className='text-center py-12'>
            <div className='w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto'></div>
            <p className='mt-2 text-gray-600'>Loading Batches...</p>
          </div>
        ) : batches.length > 0 ? (
          // Batch Grid
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {batches.map((batch) => {
              const statusDisplay = getStatusDisplay(batch.status)
              return (
                <div
                  key={batch.id}
                  className='bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200 flex flex-col justify-between'
                >
                  <div>
                    <div className='flex items-start justify-between mb-4'>
                      <h3 className='text-lg font-semibold text-gray-900 pr-2'>
                        {batch.name}
                      </h3>
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-full flex-shrink-0 ${statusDisplay.color}`}
                      >
                        {statusDisplay.label}
                      </span>
                    </div>
                    <div className='space-y-3 text-sm text-gray-600'>
                      <div className='flex items-center'>
                        <Clock className='w-4 h-4 mr-2 text-gray-400' />
                        <span>{batch.batch_time_in_hour} Hours Total</span>
                      </div>
                      <div className='flex items-center'>
                        <Timer className='w-4 h-4 mr-2 text-gray-400' />
                        <span>
                          {batch.estimated_batch_duration_in_month} Months
                          Duration
                        </span>
                      </div>
                      {batch.students !== undefined && (
                        <div className='flex items-center'>
                          <Users className='w-4 h-4 mr-2 text-gray-400' />
                          <span>{batch.students} Students</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='mt-6 flex space-x-2'>
                    <button
                      onClick={() => handleViewDetails(batch.id)}
                      className='flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-medium py-2 px-3 rounded-lg transition-colors duration-200'
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => handleUpdateClick(batch)}
                      className='bg-green-50 hover:bg-green-100 text-green-700 text-sm font-medium py-2 px-3 rounded-lg transition-colors duration-200 flex items-center justify-center'
                    >
                      <Edit className='w-4 h-4' />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(batch)}
                      className='bg-red-50 hover:bg-red-100 text-red-700 text-sm font-medium py-2 px-3 rounded-lg transition-colors duration-200 flex items-center justify-center'
                    >
                      <Trash2 className='w-4 h-4' />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          // Empty State
          <div className='text-center py-12'>
            <div className='text-gray-400 mb-4'>
              <Users className='w-16 h-16 mx-auto' />
            </div>
            <h3 className='text-lg font-medium text-gray-900 mb-2'>
              No batches found
            </h3>
            <p className='text-gray-600 mb-4'>
              Get started by creating your first batch.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className='inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200'
            >
              <Plus className='w-4 h-4 mr-2' />
              Create New Batch
            </button>
          </div>
        )}
      </main>

      {/* Create Modal Form */}
      {showForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-lg shadow-xl max-w-md w-full'>
            <form onSubmit={handleSubmit}>
              <div className='flex items-center justify-between p-6 border-b'>
                <h2 className='text-xl font-semibold text-gray-900'>
                  Create New Batch
                </h2>
                <button
                  type='button'
                  onClick={handleCancel}
                  className='text-gray-400 hover:text-gray-600 transition-colors duration-200'
                >
                  <X className='w-6 h-6' />
                </button>
              </div>

              <div className='p-6'>
                <div className='space-y-4'>
                  <div>
                    <label
                      htmlFor='name'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      Batch Name *
                    </label>
                    <input
                      type='text'
                      id='name'
                      name='name'
                      value={formData.name}
                      onChange={handleInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200'
                      placeholder='e.g., Advanced React with TypeScript'
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor='batch_time_in_hour'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      Total Hours *
                    </label>
                    <input
                      type='number'
                      id='batch_time_in_hour'
                      name='batch_time_in_hour'
                      value={formData.batch_time_in_hour}
                      onChange={handleInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200'
                      placeholder='e.g., 60'
                      min='1'
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor='estimated_batch_duration_in_month'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      Duration (Months) *
                    </label>
                    <input
                      type='number'
                      id='estimated_batch_duration_in_month'
                      name='estimated_batch_duration_in_month'
                      value={formData.estimated_batch_duration_in_month}
                      onChange={handleInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200'
                      placeholder='e.g., 4'
                      min='1'
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor='status'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      Status
                    </label>
                    <select
                      id='status'
                      name='status'
                      value={formData.status}
                      onChange={handleInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200'
                    >
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className='flex space-x-3 mt-6'>
                  <button
                    type='button'
                    onClick={handleCancel}
                    className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200'
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    disabled={isSubmitting}
                    className='flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 flex items-center justify-center'
                  >
                    {isSubmitting ? (
                      <>
                        <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2'></div>
                        Creating...
                      </>
                    ) : (
                      'Create Batch'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update Modal Form */}
      {showUpdateForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-lg shadow-xl max-w-md w-full'>
            <form onSubmit={handleUpdateSubmit}>
              <div className='flex items-center justify-between p-6 border-b'>
                <h2 className='text-xl font-semibold text-gray-900'>
                  Update Batch
                </h2>
                <button
                  type='button'
                  onClick={handleCancel}
                  className='text-gray-400 hover:text-gray-600 transition-colors duration-200'
                >
                  <X className='w-6 h-6' />
                </button>
              </div>

              <div className='p-6'>
                <div className='space-y-4'>
                  <div>
                    <label
                      htmlFor='update-name'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      Batch Name *
                    </label>
                    <input
                      type='text'
                      id='update-name'
                      name='name'
                      value={formData.name}
                      onChange={handleInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200'
                      placeholder='e.g., Advanced React with TypeScript'
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor='update-batch_time_in_hour'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      Total Hours *
                    </label>
                    <input
                      type='number'
                      id='update-batch_time_in_hour'
                      name='batch_time_in_hour'
                      value={formData.batch_time_in_hour}
                      onChange={handleInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200'
                      placeholder='e.g., 60'
                      min='1'
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor='update-estimated_batch_duration_in_month'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      Duration (Months) *
                    </label>
                    <input
                      type='number'
                      id='update-estimated_batch_duration_in_month'
                      name='estimated_batch_duration_in_month'
                      value={formData.estimated_batch_duration_in_month}
                      onChange={handleInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200'
                      placeholder='e.g., 4'
                      min='1'
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor='update-status'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      Status
                    </label>
                    <select
                      id='update-status'
                      name='status'
                      value={formData.status}
                      onChange={handleInputChange}
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200'
                    >
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className='flex space-x-3 mt-6'>
                  <button
                    type='button'
                    onClick={handleCancel}
                    className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200'
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    disabled={isSubmitting}
                    className='flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 flex items-center justify-center'
                  >
                    {isSubmitting ? (
                      <>
                        <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2'></div>
                        Updating...
                      </>
                    ) : (
                      'Update Batch'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-lg shadow-xl max-w-md w-full'>
            <div className='p-6'>
              <div className='flex items-center mb-4'>
                <div className='mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100'>
                  <Trash2 className='h-6 w-6 text-red-600' />
                </div>
              </div>
              <div className='text-center'>
                <h3 className='text-lg font-medium text-gray-900 mb-2'>
                  Delete Batch
                </h3>
                <p className='text-sm text-gray-500 mb-4'>
                  Are you sure you want to delete "{batchToDelete?.name}"? This
                  action cannot be undone.
                </p>
              </div>
              <div className='flex space-x-3'>
                <button
                  type='button'
                  onClick={() => {
                    setShowDeleteDialog(false)
                    setBatchToDelete(null)
                  }}
                  className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200'
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={handleDelete}
                  className='flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200'
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BatchPage
